import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, Bot, BarChart3, CalendarDays, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { saasApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

const fmt = (v) => v === 0 ? 'R$0' : `R$${v.toLocaleString('pt-BR')}`;

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const { user, firm } = useAuth();

  useEffect(() => { saasApi.plans().then(setPlans).catch(() => {}); }, []);

  const currentPlan = firm?.plan || null;

  return (
    <div>
      <SEO title="Planos para escritórios de advocacia" description="Assinatura fixa de presença digital: perfil verificado, recepcionista com IA, CRM, agenda e analytics. Sem venda de leads, sem comissão sobre honorários." />
      <section className="bg-white border-b border-[#cbd8dc] text-[#122f36] py-14 md:py-16">
        <div className="aj-wrap max-w-[900px] text-center">
          <div className="inline-flex items-center gap-2 bg-[#edf4f5] border border-[#cbd8dc] rounded-full px-4 py-1.5 text-xs font-medium text-[#075a6d] mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Assinatura fixa • Sem comissão sobre honorários • Sem venda de leads
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold">Sua presença digital jurídica, <span className="text-[#075a6d]">completa e mensurável</span></h1>
          <p className="mt-4 text-lg text-[#354c54] max-w-2xl mx-auto">Perfil verificado, recepcionista com IA, CRM, agenda e analytics. Cancele quando quiser, direto no painel.</p>
        </div>
      </section>

      <div className="aj-wrap -mt-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map(p => {
            const isCurrent = currentPlan === p.id;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`} className={`bg-white rounded-[10px] border-2 p-6 flex flex-col relative ${p.highlight ? 'border-[#2f5d65]' : 'border-[#cbd8dc]'}`}>
                {p.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#153e44] text-white text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full">Mais escolhido</div>}
                <div className="text-xs font-semibold text-[#075a6d] uppercase tracking-widest">{p.tagline}</div>
                <h3 className="text-xl font-semibold text-[#153e44] mt-1">{p.name}</h3>
                <div className="mt-3 flex items-end gap-1">
                  <span className="text-3xl font-semibold text-[#153e44]">{fmt(p.price)}</span>
                  {p.price > 0 && <span className="text-sm text-[#52676e] mb-1">/mês</span>}
                </div>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#354c54]">
                      <Check className="w-4 h-4 text-[#075a6d] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {isCurrent ? (
                    <Button disabled className="w-full" variant="outline" data-testid={`plan-current-${p.id}`}>Plano atual</Button>
                  ) : p.id === 'gratuito' ? (
                    <Link to={user ? '/painel' : '/cadastro'}><Button variant="outline" className="w-full" data-testid={`plan-cta-${p.id}`}>Começar grátis</Button></Link>
                  ) : (
                    <Link to={user && firm ? `/checkout/${p.id}` : '/cadastro'}>
                      <Button className={`w-full ${p.highlight ? 'bg-[#153e44] hover:bg-[#0c3036] text-white' : 'bg-[#153e44] hover:bg-[#0c3036]'}`} data-testid={`plan-cta-${p.id}`}>Assinar {p.name}</Button>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Bot, title: 'Recepcionista com IA', desc: 'Triagem administrativa 24h com consentimento explícito. Sem parecer jurídico — só organização.' },
            { icon: BarChart3, title: 'CRM e analytics reais', desc: 'Pipeline de contatos, tarefas, notas e métricas de impressões e visitas sem números inventados.' },
            { icon: CalendarDays, title: 'Agenda integrada', desc: 'Agendamentos com confirmação, lembrete e reagendamento no mesmo painel.' },
          ].map((b, i) => (
            <div key={i} className="bg-white border border-[#cbd8dc] rounded-[10px] p-6">
              <div className="w-11 h-11 rounded-[7px] bg-[#153e44] flex items-center justify-center mb-3"><b.icon className="w-5 h-5 text-[#075a6d]" /></div>
              <h3 className="font-semibold text-[#153e44]">{b.title}</h3>
              <p className="text-sm text-[#425a62] mt-1">{b.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-[#f2f6f7] border border-[#cbd8dc] rounded-[10px] p-5 text-sm text-[#425a62] flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-[#075a6d] mt-0.5 shrink-0" />
          <p>Princípios: pagamento não altera a posição orgânica nas buscas. Destaques patrocinados são sempre identificados. Nenhum valor é percentual de honorários ou condicionado a resultados. <span className="font-semibold">Checkout em modo demonstração (nenhum valor é cobrado).</span></p>
        </div>
      </div>
    </div>
  );
}
